/** Error reporting tunnel to the edge's /monitoring route, which forwards to
 * Sentry — sentry.io itself is commonly ad-blocked, and ad-blocker users are
 * disproportionately the ones with broken logins. Fire-and-forget: reporting
 * must never affect the sign-in flow, so every failure here is swallowed.
 *
 * Never send emails, OTP codes, passwords, or request bodies. `error.message`
 * here is always either one the api/ layer already promises is safe to show
 * a user (see ui/api/auth.ts) or a fixed string this module writes itself.
 */

const MAX_REPORTS_PER_LOAD = 5;
let sent = 0;

export function reportError(
  origin: string,
  step: string,
  error: unknown,
  status?: number,
): void {
  if (sent >= MAX_REPORTS_PER_LOAD) return;
  sent++;
  const body = {
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    step,
    status,
    pageOrigin: typeof window !== "undefined" ? window.location.origin : "",
  };
  try {
    void fetch(`${origin}/monitoring`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* reporting must never break the flow */
  }
}

/** Handlers for the edge's own pages, filtered to code the edge served — the
 * same rule as the widget below, for the same reason. These pages also load the
 * analytics loader, the consent manager from unpkg and whatever extensions the
 * visitor runs; an uncaught error from any of them is unactionable here, and
 * together they outnumbered every real error in the project.
 *
 * `reportOrigin` is only where reports are *sent* ("" — these pages are
 * same-origin with /monitoring). What counts as first-party is the page's own
 * origin, which is what served its bundle. */
export function installGlobalErrorReporting(reportOrigin: string): void {
  if (typeof window === "undefined") return;
  const pageOrigin = window.location.origin;
  window.addEventListener("error", (e) => {
    if (isOpaqueCrossOrigin(e)) return;
    if (isDeclinedSignature(e)) return;
    if (!isFromOrigin(e.error, pageOrigin, e.filename)) return;
    reportError(reportOrigin, "page.uncaught", e.error ?? e.message);
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (!isFromOrigin(e.reason, pageOrigin)) return;
    reportError(reportOrigin, "page.unhandled_rejection", e.reason);
  });
}

/** The same handlers for the embedded widget, which shares `window` with a host
 * page we don't own: only failures traceable to code served from `edgeOrigin`
 * are reported, or every uncaught error on a consumer site would land in the
 * identity project.
 *
 * Uncaught errors from the bundle are opaque to a host-page listener ("Script
 * error.", no filename or stack) unless its <script> opts in with `crossorigin`
 * and the edge answers with Access-Control-Allow-Origin — neither is true
 * today, so in practice this reports rejections cross-origin and both on the
 * edge's own origin. Filtered rather than unfiltered: silence beats noise. */
export function installWidgetErrorReporting(edgeOrigin: string): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    if (isOpaqueCrossOrigin(e)) return;
    if (isDeclinedSignature(e)) return;
    if (!isFromOrigin(e.error, edgeOrigin, e.filename)) return;
    reportError(edgeOrigin, "widget.uncaught", e.error ?? e.message);
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (!isFromOrigin(e.reason, edgeOrigin)) return;
    reportError(edgeOrigin, "widget.unhandled_rejection", e.reason);
  });
}

/** The fixed message a browser substitutes when a cross-origin script throws
 * without `crossorigin` + CORS: no line, no stack, and an `error` that is
 * absent or stackless. Firefox still fills `filename` with the *document*, so
 * this has to be declined by its signature — attribution can't do it, and the
 * document is always same-origin. A stack is the one thing that can outvote the
 * signature, because it names the script that actually threw. */
function isOpaqueCrossOrigin(e: ErrorEvent): boolean {
  if (!/^Script error\.?$/.test(e.message ?? "")) return false;
  return !(e.error instanceof Error && e.error.stack);
}

/** Signatures that arrive as an uncaught error but name nothing this code can
 * act on, and that attribution cannot decline for us.
 *
 * The ResizeObserver messages are a browser condition rather than a fault: the
 * spec has the browser say this when a resize callback dirties layout again,
 * and the following frame settles it. They carry no stack, so `filename` is the
 * document — always same-origin, so the origin filter passes them.
 *
 * `SCDynimacBridge` is an in-app browser's injected bridge, which runs inline
 * in our document: it is first-party by every signal except its name.
 *
 * The regex message is PostHog's session-recorder matching a page URL against
 * its recording rules; the widget's own `open()` only appears in the stack
 * because it synchronously focuses an element that the recorder's listener
 * happens to run off of, so the origin filter sees a first-party frame for a
 * fault that is entirely third-party and unfixable here.
 *
 * Exact matches only, and one proven signature at a time — a pattern here is a
 * silenced category.
 */
const DECLINED_MESSAGES = new Set([
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
  "Can't find variable: SCDynimacBridge",
  "Invalid regular expression: regular expression too large",
]);

function isDeclinedSignature(e: ErrorEvent): boolean {
  const fromError = e.error instanceof Error ? e.error.message : "";
  return (
    DECLINED_MESSAGES.has(e.message ?? "") || DECLINED_MESSAGES.has(fromError)
  );
}

function isFromOrigin(
  error: unknown,
  origin: string,
  filename?: string,
): boolean {
  const stack = error instanceof Error ? error.stack : undefined;
  // Parsed-origin equality per URL in the stack, never a prefix match.
  const stackUrls = stack?.match(/https?:\/\/[^\s)]+/g) ?? [];
  if (stackUrls.some((url) => hasOrigin(url, origin))) return true;
  // A stack naming only other origins settles it: `filename` is the document in
  // Firefox, so a third-party script's throw arrives looking same-origin.
  if (stackUrls.length) return false;
  return hasOrigin(filename, origin);
}

function hasOrigin(url: string | undefined, origin: string): boolean {
  if (!url) return false;
  try {
    return new URL(url).origin === origin;
  } catch {
    return false;
  }
}
